import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useLeaderPortalService,
  type LeaderCaseView,
  type LeaderDashboardStats,
  type LeaderSessionView,
} from '@actschurch/ccms-sdk';
import { DASHBOARD_POLL_MS, STALE_DAYS } from '../constants';

/**
 * useLeaderDashboard — everything the "My Care" half of the Dashboard needs.
 *
 * Short-circuits entirely when `enabled` is false, so a facilitator-only user never fires
 * a leader request. Uses `Promise.allSettled` throughout: a user can hold the
 * `ActsCareLeader` role claim without a `Leader` record existing, and a 403 on one call
 * must hide its card rather than fail the page.
 *
 * `myUnviewedCases()` is specced in `_deploy/_newEndPoints/ac-counselling-overview.md`
 * and detected by capability — until the SDK ships it, `unviewedCases` stays null and the
 * attention band omits that item rather than showing a wrong zero.
 */
export interface UseLeaderDashboardResult {
  stats: LeaderDashboardStats | null;
  cases: LeaderCaseView[];
  pendingCases: LeaderCaseView[];
  sessions: LeaderSessionView[];
  /** Null when the SDK has no `myUnviewedCases()` — not the same as "none". */
  unviewedCases: LeaderCaseView[] | null;
  /** My cases with no recent activity, most neglected first. */
  staleCases: LeaderCaseView[];
  sessionsToday: LeaderSessionView[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const EMPTY: UseLeaderDashboardResult = {
  stats: null,
  cases: [],
  pendingCases: [],
  sessions: [],
  unviewedCases: null,
  staleCases: [],
  sessionsToday: [],
  loading: false,
  refresh: async () => {},
};

export function useLeaderDashboard(enabled: boolean): UseLeaderDashboardResult {
  const leaderPortal = useLeaderPortalService();

  const [stats, setStats] = useState<LeaderDashboardStats | null>(null);
  const [cases, setCases] = useState<LeaderCaseView[]>([]);
  const [pendingCases, setPendingCases] = useState<LeaderCaseView[]>([]);
  const [sessions, setSessions] = useState<LeaderSessionView[]>([]);
  const [unviewedCases, setUnviewedCases] = useState<LeaderCaseView[] | null>(null);
  const [loading, setLoading] = useState(enabled);

  const fetchAll = useCallback(async (showLoading = true) => {
    if (!enabled) return;
    if (showLoading) setLoading(true);

    const portal = leaderPortal as any;

    try {
      const [statsRes, casesRes, pendingRes, sessionsRes, unviewedRes] = await Promise.allSettled([
        leaderPortal.dashboardStats(),
        leaderPortal.myCases(),
        leaderPortal.myPendingCases(),
        leaderPortal.mySessions(),
        typeof portal.myUnviewedCases === 'function'
          ? portal.myUnviewedCases()
          : Promise.reject(new Error('myUnviewedCases() not in this SDK version')),
      ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value);
      if (casesRes.status === 'fulfilled') setCases(casesRes.value ?? []);
      if (pendingRes.status === 'fulfilled') setPendingCases(pendingRes.value ?? []);
      if (sessionsRes.status === 'fulfilled') setSessions(sessionsRes.value ?? []);
      setUnviewedCases(unviewedRes.status === 'fulfilled' ? (unviewedRes.value ?? []) : null);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [enabled, leaderPortal]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchRef = useRef(fetchAll);
  fetchRef.current = fetchAll;

  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => { fetchRef.current(false); }, DASHBOARD_POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled]);

  const staleCases = useMemo(
    () => cases
      .filter((c) => (c.daysSinceLastActivity ?? 0) >= STALE_DAYS)
      .sort((a, b) => (b.daysSinceLastActivity ?? 0) - (a.daysSinceLastActivity ?? 0)),
    [cases],
  );

  const sessionsToday = useMemo(() => sessions.filter((s) => s.isToday), [sessions]);

  const refresh = useCallback(() => fetchAll(false), [fetchAll]);

  if (!enabled) return EMPTY;

  return {
    stats, cases, pendingCases, sessions, unviewedCases,
    staleCases, sessionsToday, loading, refresh,
  };
}

export default useLeaderDashboard;
